'use client';

import { useRef, useState } from 'react';
import { Bell, BellRing, X, AlertTriangle, Clock, Calendar } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useRealTimeUpdates } from '@/hooks/useRealTimeUpdates';
import { toast } from 'sonner';

interface OverdueRental {
  id: string;
  customer: {
    id: string;
    name: string;
    phone: string;
  };
  rentalDate: string;
  expectedReturnDate: string;
  overdueDays: number;
  totalAmount: number;
  coats: {
    id: string;
    name: string;
    type: string;
  }[];
}

interface NotificationSystemProps {
  enabled?: boolean;
  checkInterval?: number; // in minutes
}

export function NotificationSystem({ enabled = true, checkInterval = 5 }: NotificationSystemProps) {
  const [notifications, setNotifications] = useState<OverdueRental[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const lastCountRef = useRef(0);

  useRealTimeUpdates<OverdueRental[]>(
    async () => {
      const response = await fetch('/api/rentals?status=overdue');
      if (!response.ok) throw new Error('Failed to fetch overdue rentals');
      const data = await response.json();
      return data.data?.rentals || [];
    },
    {
      interval: checkInterval * 60 * 1000,
      enabled,
      onUpdate: (overdueRentals) => {
        setNotifications(overdueRentals);
        setUnreadCount(overdueRentals.length);

        const prev = lastCountRef.current;
        const next = overdueRentals.length;
        if (next > prev) {
          const delta = next - prev;
          toast.warning(`${delta} new overdue rental${delta > 1 ? 's' : ''}`, {
            description: 'Some rentals are past their return date',
            action: { label: 'View', onClick: () => setIsOpen(true) },
          });
        }
        lastCountRef.current = next;
      },
    }
  );

  const markAsRead = () => {
    setUnreadCount(0);
  };

  const handleViewRental = (rentalId: string) => {
    window.open(`/rentals/${rentalId}`, '_blank');
    markAsRead();
  };

  const handleViewAllOverdue = () => {
    window.open('/rentals?status=overdue', '_blank');
    markAsRead();
    setIsOpen(false);
  };

  if (!enabled) return null;

  return (
    <div className="relative">
      {/* Notification Bell */}
      <button
        onClick={() => {
          setIsOpen(!isOpen);
          markAsRead();
        }}
        className="relative p-2 rounded-lg hover:bg-muted/50 transition-colors"
        title={`Notifications${unreadCount > 0 ? ` (${unreadCount} new)` : ''}`}
      >
        {unreadCount > 0 ? (
          <BellRing className="h-5 w-5 text-orange-500" />
        ) : (
          <Bell className="h-5 w-5 text-muted-foreground" />
        )}
        {unreadCount > 0 && (
          <Badge 
            variant="destructive" 
            className="absolute -top-1 -right-1 h-5 w-5 rounded-full p-0 text-xs flex items-center justify-center"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </Badge>
        )}
      </button>

      {/* Notification Dropdown */}
      {isOpen && (
        <div className="absolute right-0 top-12 w-96 bg-background border border-border rounded-lg shadow-lg z-50 max-h-96 overflow-hidden">
          <Card className="border-0 shadow-none">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium">Notifications</CardTitle>
                <div className="flex items-center gap-2">
                  {notifications.length > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleViewAllOverdue}
                      className="text-xs"
                    >
                      View All
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsOpen(false)}
                    className="h-6 w-6 p-0"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {notifications.length === 0 ? (
                <div className="p-6 text-center text-muted-foreground">
                  <Bell className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No new notifications</p>
                  <p className="text-xs">All rentals are on time</p>
                </div>
              ) : (
                <div className="max-h-80 overflow-y-auto">
                  {notifications.map((rental) => (
                    <div
                      key={rental.id}
                      className="p-4 border-b border-border hover:bg-muted/30 transition-colors cursor-pointer"
                      onClick={() => handleViewRental(rental.id)}
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0">
                          <AlertTriangle className="h-5 w-5 text-orange-500 mt-0.5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <p className="text-sm font-medium truncate">
                              {rental.customer.name}
                            </p>
                            <Badge variant="outline" className="text-xs">
                              {rental.overdueDays} day{rental.overdueDays > 1 ? 's' : ''} overdue
                            </Badge>
                          </div>
                          <div className="space-y-1 text-xs text-muted-foreground">
                            <div className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              <span>Due: {new Date(rental.expectedReturnDate).toLocaleDateString()}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              <span>Late by {rental.overdueDays} day{rental.overdueDays > 1 ? 's' : ''}</span>
                            </div>
                            <p className="truncate">
                              {rental.coats.length} coat{rental.coats.length > 1 ? 's' : ''}: {rental.coats.map(c => c.name).join(', ')}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

// Standalone notification component for dashboard
export function OverdueNotifications() {
  const [overdueRentals, setOverdueRentals] = useState<OverdueRental[]>([]);

  useRealTimeUpdates<OverdueRental[]>(
    async () => {
      const response = await fetch('/api/rentals?status=overdue');
      if (!response.ok) throw new Error('Failed to fetch overdue rentals');
      const data = await response.json();
      return data.data?.rentals || [];
    },
    {
      interval: 15 * 60 * 1000,
      onUpdate: (rentals) => setOverdueRentals(rentals),
    }
  );

  if (overdueRentals.length === 0) {
    return (
      <div className="rounded-lg bg-green-50 dark:bg-green-900/20 p-4 border border-green-200 dark:border-green-800">
        <div className="flex items-center gap-2 text-green-700 dark:text-green-300">
          <Bell className="h-4 w-4" />
          <span className="text-sm font-medium">All rentals on time</span>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg bg-orange-50 dark:bg-orange-900/20 p-4 border border-orange-200 dark:border-orange-800">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-orange-700 dark:text-orange-300">
          <AlertTriangle className="h-4 w-4" />
          <span className="text-sm font-medium">
            {overdueRentals.length} overdue rental{overdueRentals.length > 1 ? 's' : ''}
          </span>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => window.open('/rentals?status=overdue', '_blank')}
          className="text-xs"
        >
          View Details
        </Button>
      </div>
      <div className="space-y-2">
        {overdueRentals.slice(0, 3).map((rental) => (
          <div
            key={rental.id}
            className="text-xs text-orange-600 dark:text-orange-400 flex items-center justify-between"
          >
            <span className="truncate">{rental.customer.name}</span>
            <span>{rental.overdueDays} day{rental.overdueDays > 1 ? 's' : ''}</span>
          </div>
        ))}
        {overdueRentals.length > 3 && (
          <div className="text-xs text-orange-600 dark:text-orange-400">
            +{overdueRentals.length - 3} more...
          </div>
        )}
      </div>
    </div>
  );
}
